/**
 * "Lupa can help" tips for Home → My work (Jon, 2026-11-03): ways Lupa can help get
 * a task or deal done (research, draft emails, pull up SOPs…). Each tip opens the
 * Lupa widget with the prompt filled in, never sent without the person pressing send.
 *
 * Keyword rules, not an LLM call: instant, free, predictable. Every prompt names
 * the task (and project) so Lupa has the context; Lupa's tools decide what it can
 * actually read for this person.
 */
import type { MyWorkDeal, MyWorkTask } from '../services/homeApi';

export interface LupaTip {
  label: string;
  prompt: string;
}

type TaskRule = { match: RegExp; tip: (subject: string) => LupaTip };

const TASK_RULES: TaskRule[] = [
  {
    match: /\b(email|e-mail|follow[- ]?up|reach out|reply|respond|outreach|client update|send)\b/i,
    tip: (s) => ({ label: 'Draft the email', prompt: `Draft a short, friendly email for my task ${s}. Ask me for anything you need to fill in.` }),
  },
  {
    match: /\b(blog|post|copy|content|article|newsletter|caption|write|writing|landing page)\b/i,
    tip: (s) => ({ label: 'Outline or draft it', prompt: `Help me write for my task ${s}: suggest an outline, then a first draft in DWA's voice.` }),
  },
  {
    match: /\b(seo|keyword|research|audit|competitor|analysis|analyze|review|benchmark)\b/i,
    tip: (s) => ({ label: 'Research it', prompt: `Do some research for my task ${s}. Summarize what matters and list sources or next steps.` }),
  },
  {
    match: /\b(report|recap|scorecard|monthly|weekly|quarterly|summary|metrics|dashboard)\b/i,
    tip: (s) => ({ label: 'Pull the numbers', prompt: `Pull together the numbers for my task ${s} from the data you can see, and draft a short summary.` }),
  },
  {
    match: /\b(social|facebook|instagram|linkedin|ads?|campaign|media|banner|promo)\b/i,
    tip: (s) => ({ label: 'Brainstorm ideas', prompt: `Brainstorm ideas for my task ${s}: a few angles, headlines or post ideas I can pick from.` }),
  },
  {
    match: /\b(launch|qa|setup|set up|onboard|onboarding|migrat\w*|plugin|wordpress|site|install|backup|dns|ssl|process)\b/i,
    tip: (s) => ({ label: 'Find the SOP', prompt: `Find any SOPs in the Knowledgebase that cover my task ${s}, and turn them into a short checklist.` }),
  },
  {
    match: /\b(meeting|call|kickoff|kick-off|presentation|pitch|prep)\b/i,
    tip: (s) => ({ label: 'Prep me', prompt: `Help me prepare for my task ${s}: key points, questions to ask and anything I should look up first.` }),
  },
];

const sopTip = (s: string): LupaTip => ({
  label: 'Find the SOP',
  prompt: `Is there an SOP in the Knowledgebase for my task ${s}? If so, summarize the steps.`,
});

const stepsTip = (s: string): LupaTip => ({
  label: 'Break it into steps',
  prompt: `Break my task ${s} into small steps with a rough time for each, so I can plan it into my week.`,
});

function subject(task: Pick<MyWorkTask, 'name' | 'project'>): string {
  return task.project ? `"${task.name}" (project: ${task.project})` : `"${task.name}"`;
}

/** Up to `max` tips for a Teamwork task: keyword matches first, then SOP and steps. */
export function tipsForTask(task: Pick<MyWorkTask, 'name' | 'project'>, max = 3): LupaTip[] {
  const s = subject(task);
  const text = `${task.name} ${task.project ?? ''}`;
  const tips: LupaTip[] = [];
  for (const rule of TASK_RULES) if (rule.match.test(text)) tips.push(rule.tip(s));
  for (const fallback of [sopTip(s), stepsTip(s)]) tips.push(fallback);
  const seen = new Set<string>();
  return tips.filter((t) => !seen.has(t.label) && seen.add(t.label)).slice(0, max);
}

/** A deal with no stage change for this many days counts as stalled. */
export const STALLED_DAYS = 21;

function daysSince(iso: string | null | undefined, today: Date): number | null {
  if (!iso) return null;
  const t = new Date(iso).getTime();
  return Number.isNaN(t) ? null : Math.floor((today.getTime() - t) / 86_400_000);
}

/**
 * Up to `max` tips for an open HubSpot deal, picked by what would move it along:
 * past its close date or stalled first, then by stage, then company research.
 */
export function tipsForDeal(
  deal: Pick<MyWorkDeal, 'name' | 'stage' | 'close_date' | 'stage_entered_at'>,
  today: Date = new Date(),
  max = 3,
): LupaTip[] {
  const s = `"${deal.name}"${deal.stage ? ` (stage: ${deal.stage})` : ''}`;
  const stage = (deal.stage ?? '').toLowerCase();
  const tips: LupaTip[] = [];
  const pastClose = deal.close_date ? new Date(`${deal.close_date.slice(0, 10)}T23:59:59`) < today : false;
  const inStage = daysSince(deal.stage_entered_at, today);

  if (pastClose) {
    tips.push({
      label: 'Past close date: check in',
      prompt: `My HubSpot deal ${s} is past its close date. Draft a short check-in email to re-engage them, and suggest a realistic new close date.`,
    });
  }
  if (inStage !== null && inStage >= STALLED_DAYS) {
    tips.push({
      label: `Stalled ${inStage} days: re-engage`,
      prompt: `My HubSpot deal ${s} hasn't moved in ${inStage} days. Suggest a few ways to re-engage and draft a follow-up email.`,
    });
  }
  if (/appointment|discovery|qualif|lead|connect|intro/.test(stage)) {
    tips.push({ label: 'Prep the discovery call', prompt: `Help me prep a discovery call for my HubSpot deal ${s}: goals, questions to ask, and how DWA's services might fit.` });
  } else if (/proposal|quote|present|pricing|scope/.test(stage)) {
    tips.push({ label: 'Follow up on the proposal', prompt: `Draft a follow-up on the proposal for my HubSpot deal ${s}, and list likely objections with how to answer them.` });
  } else if (/contract|negotiat|decision|commit|verbal/.test(stage)) {
    tips.push({ label: 'Close it out', prompt: `Help me close my HubSpot deal ${s}: draft a next-steps email and a short checklist to get it signed.` });
  } else {
    tips.push({ label: 'Draft a follow-up', prompt: `Draft a follow-up email for my HubSpot deal ${s} to move it to the next stage.` });
  }
  tips.push({
    label: 'Research the company',
    prompt: `Research the company behind my HubSpot deal ${s}: what they do, recent news, and talking points for my next conversation.`,
  });
  return tips.slice(0, max);
}

/** General tips for the whole My work panel ("what's on my plate"). */
export const GENERAL_TIPS: LupaTip[] = [
  { label: 'Plan my week', prompt: "What's on my plate this week? Suggest an order to tackle it, and flag anything I might not get to." },
  { label: 'Find an SOP', prompt: 'Find the SOP for ' },
  { label: 'Draft an email', prompt: 'Draft an email to ' },
  { label: 'Research something', prompt: 'Research ' },
];
